import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  Receipt as ReceiptIcon,
  Add as AddIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  MapsHomeWork as MapsHomeWorkIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import Footer from './Footer';

const drawerWidth = 240;

function Layout({ children, onLogout, user }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [companyName, setCompanyName] = useState('AppliFactuWeb');
  const [companyInfo, setCompanyInfo] = useState({
    nom: 'AppliFactuWeb',
    adresse: '',
    telephone: '',
    email: ''
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  // Charger les informations de la société depuis les préférences
  useEffect(() => {
    const loadCompanyInfo = async () => {
      try {
        const response = await fetch('/api/preferences', {
          method: 'GET',
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.nom) {
            setCompanyName(data.nom);
            setCompanyInfo(data);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des informations de la société:', error);
      }
    };

    loadCompanyInfo();
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    onLogout();
  };

  const menuItemsAll = [
    { text: 'Tableau de bord', icon: <DashboardIcon />, path: '/' },
    { text: 'Clients', icon: <PeopleIcon />, path: '/clients' },
    { text: 'Clients du jour', icon: <PeopleIcon />, path: '/clients/journee' },
    {
      text: 'Maison de repos',
      icon: <MapsHomeWorkIcon />,
      path: '/maisons-repos',
      adminOnly: true,
    },
    { text: 'Articles', icon: <InventoryIcon />, path: '/articles' },
    { text: 'Factures', icon: <ReceiptIcon />, path: '/factures' },
    { text: 'Nouvelle Facture', icon: <AddIcon />, path: '/factures/nouvelle' },
    { text: 'Envoi Peppol', icon: <SendIcon />, path: '/peppol' },
  ];
  const menuItems = menuItemsAll.filter(
    (item) =>
      !item.adminOnly || user?.role === 'admin' || user?.role === 'superadmin'
  );

  const drawer = (
    <Box>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <img 
            src="/logo.png" 
            alt="Logo" 
            style={{ 
              height: '40px', 
              width: 'auto',
              maxWidth: '180px'
            }} 
          />
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ flexGrow: 1 }}>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => {
              navigate(item.path);
              if (isMobile) {
                setMobileOpen(false);
              }
            }}
            selected={location.pathname === item.path}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
      {/* Informations de la société en bas */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          {companyInfo.nom}
        </Typography>
        {companyInfo.adresse && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
            {companyInfo.adresse}
          </Typography>
        )}
        {companyInfo.telephone && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
            {companyInfo.telephone}
          </Typography>
        )}
        {companyInfo.email && (
          <Typography variant="body2" color="textSecondary">
            {companyInfo.email}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img 
              src="/logo-pariscoiff.jpg" 
              alt="Logo Pariscoiff" 
              style={{ 
                height: '40px', 
                width: 'auto',
                marginRight: '16px'
              }} 
            />
            <Typography variant="h6" noWrap component="div">
              {companyName}
            </Typography>
          </Box>

          {/* Bouton Utilisateurs pour les admins et superadmins */}
          {(user?.role === 'admin' || user?.role === 'superadmin') && (
            <Button
              color="inherit"
              onClick={() => navigate('/utilisateurs')}
              sx={{ mr: 2 }}
            >
              Utilisateurs
            </Button>
          )}

          {/* Bouton Préférences - Visible pour les administrateurs et superadmins */}
          {(user?.role === 'admin' || user?.role === 'superadmin') && (
            <Button
              color="inherit"
              onClick={() => navigate('/preferences')}
              sx={{ mr: 2 }}
            >
              Préférences
            </Button>
          )}

          {/* Menu utilisateur */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              {user?.username}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <AccountCircleIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Déconnexion
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Navigation horizontale juste en dessous de l'AppBar */}
      <Box
        sx={{
          position: 'fixed',
          top: '64px', // Hauteur de l'AppBar
          left: 0,
          right: 0,
          zIndex: theme.zIndex.drawer,
          backgroundColor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1, gap: 1 }}>
          <Button
            color="inherit"
            onClick={() => navigate('/')}
            sx={{
              color: location.pathname === '/' ? 'primary.main' : 'text.secondary',
              borderBottom: location.pathname === '/' ? 2 : 0,
              borderColor: 'primary.main',
              borderRadius: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                color: 'primary.main',
              },
            }}
          >
            Tableau de bord
          </Button>
          <Button
            color="inherit"
            onClick={() => navigate('/clients')}
            sx={{
              color: location.pathname === '/clients' ? 'primary.main' : 'text.secondary',
              borderBottom: location.pathname === '/clients' ? 2 : 0,
              borderColor: 'primary.main',
              borderRadius: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                color: 'primary.main',
              },
            }}
          >
            Clients
          </Button>
          <Button
            color="inherit"
            onClick={() => navigate('/clients/journee')}
            sx={{
              color: location.pathname === '/clients/journee' ? 'primary.main' : 'text.secondary',
              borderBottom: location.pathname === '/clients/journee' ? 2 : 0,
              borderColor: 'primary.main',
              borderRadius: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                color: 'primary.main',
              },
            }}
          >
            Clients du jour
          </Button>
          {(user?.role === 'admin' || user?.role === 'superadmin') && (
            <Button
              color="inherit"
              onClick={() => navigate('/maisons-repos')}
              sx={{
                color: location.pathname === '/maisons-repos' ? 'primary.main' : 'text.secondary',
                borderBottom: location.pathname === '/maisons-repos' ? 2 : 0,
                borderColor: 'primary.main',
                borderRadius: 0,
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: 'primary.main',
                },
              }}
            >
              Maison de repos
            </Button>
          )}
          <Button
            color="inherit"
            onClick={() => navigate('/articles')}
            sx={{
              color: location.pathname === '/articles' ? 'primary.main' : 'text.secondary',
              borderBottom: location.pathname === '/articles' ? 2 : 0,
              borderColor: 'primary.main',
              borderRadius: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                color: 'primary.main',
              },
            }}
          >
            Articles
          </Button>
          <Button
            color="inherit"
            onClick={() => navigate('/factures')}
            sx={{
              color: location.pathname === '/factures' ? 'primary.main' : 'text.secondary',
              borderBottom: location.pathname === '/factures' ? 2 : 0,
              borderColor: 'primary.main',
              borderRadius: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                color: 'primary.main',
              },
            }}
          >
            Factures
          </Button>
          <Button
            color="inherit"
            onClick={() => navigate('/factures/nouvelle')}
            sx={{
              color: location.pathname === '/factures/nouvelle' ? 'primary.main' : 'text.secondary',
              borderBottom: location.pathname === '/factures/nouvelle' ? 2 : 0,
              borderColor: 'primary.main',
              borderRadius: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                color: 'primary.main',
              },
            }}
          >
            Nouvelle facture
          </Button>
          <Button
            color="inherit"
            onClick={() => navigate('/peppol')}
            startIcon={<SendIcon sx={{ fontSize: 18 }} />}
            sx={{
              color: location.pathname === '/peppol' ? 'primary.main' : 'text.secondary',
              borderBottom: location.pathname === '/peppol' ? 2 : 0,
              borderColor: 'primary.main',
              borderRadius: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                color: 'primary.main',
              },
            }}
          >
            Envoi Peppol
          </Button>
        </Box>
      </Box>

      {/* Drawer mobile uniquement */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth 
          },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 1,
          width: '100%',
          mt: { xs: '64px', md: '120px' }, // Espace pour AppBar + navigation
          minHeight: 'calc(100vh - 120px)',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f5f5f5',
        }}
      >
        <Box sx={{ 
          flexGrow: 1,
          p: 2,
          backgroundColor: 'white',
          borderRadius: 1,
          boxShadow: 1,
          height: '100%',
        }}>
          {children}
        </Box>
        <Footer />
      </Box>
    </Box>
  );
}

export default Layout; 